import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRightIcon,
  BookOpenIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { fetchCompany } from "../../../api/company";
import { companyProfileIssues, normalizeCompanyRecord } from "./setupGuideStatus";

const GuideTaskRow = ({ icon: Icon, title, status, description, to, linkLabel }) => {
  const tone =
    status === "ok"
      ? "border-emerald-200 bg-emerald-50/50"
      : status === "warn"
        ? "border-amber-200 bg-amber-50/40"
        : "border-slate-200 bg-slate-50/60";

  const StatusIcon =
    status === "ok" ? CheckCircleIcon : status === "warn" ? ExclamationTriangleIcon : InformationCircleIcon;

  const statusColor =
    status === "ok" ? "text-emerald-700" : status === "warn" ? "text-amber-800" : "text-slate-600";

  return (
    <div
      className={`flex flex-col gap-3 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${tone}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className={`mt-0.5 flex items-center gap-1.5 text-xs font-medium ${statusColor}`}>
            <StatusIcon className="h-4 w-4 shrink-0" />
            <span>{description}</span>
          </div>
        </div>
      </div>
      {to ? (
        <Link
          to={to}
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
        >
          {linkLabel}
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      ) : null}
    </div>
  );
};

export const CompanyGuideCompanySection = () => {
  const { t } = useTranslation();
  const [company, setCompany] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadError(false);
      try {
        const res = await fetchCompany();
        if (cancelled) return;
        const normalized = normalizeCompanyRecord(res) ?? res;
        const ok = normalized && typeof normalized === "object" && normalized.id != null && !normalized.errors;
        if (!ok) {
          setLoadError(true);
          setCompany(null);
        } else {
          setCompany(normalized);
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setCompany(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loaded = company !== null;
  const issueSet = useMemo(
    () => new Set(loaded ? companyProfileIssues(company) : []),
    [company, loaded]
  );

  const tasks = useMemo(() => {
    const row = (id, warnKey, okKey, to, linkKey) => ({
      id,
      icon: BuildingOffice2Icon,
      title: t(`company_guide.company.task.${id}.title`),
      status: issueSet.has(id) ? "warn" : "ok",
      description: t(
        issueSet.has(id) ? `company_guide.company.task.${id}.${warnKey}` : `company_guide.company.task.${id}.${okKey}`
      ),
      to,
      linkLabel: t(`company_guide.company.link.${linkKey}`),
    });

    if (!loaded) return [];

    return [
      row("name", "warn", "ok", "/dashboard/company/settings#guide-company-profile", "settings"),
      row("logo", "warn", "ok", "/dashboard/company/settings#guide-company-logo", "settings"),
      row("contact", "warn", "ok", "/dashboard/company/settings#guide-company-contact", "settings"),
      row("address", "warn", "ok", "/dashboard/company/settings#guide-company-addresses", "settings"),
      row(
        "sales_conditions_url",
        "warn",
        "ok",
        "/dashboard/company/settings#guide-company-links",
        "settings"
      ),
      row("vat", "warn", "ok", "/dashboard/company/settings#guide-company-profile", "settings"),
    ];
  }, [issueSet, loaded, t]);

  const doneCount = tasks.filter((r) => r.status === "ok").length;
  const warnCount = tasks.filter((r) => r.status === "warn").length;

  return (
    <section
      id="company-setup-status"
      className="scroll-mt-24 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5"
    >
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 shadow-sm">
            <BookOpenIcon className="h-4 w-4" />
            {t("company_guide.company.badge")}
          </div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-950">
            {t("company_guide.company.heading")}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">{t("company_guide.company.subtitle")}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
            {t("company_guide.company.progress_label")}
          </div>
          <div className="mt-1 font-semibold text-slate-900">
            {loadError || issueSet.has("load")
              ? t("company_guide.company.progress_error")
              : company === null
                ? t("company_guide.company.progress_loading")
                : warnCount > 0
                  ? t("company_guide.company.progress_warnings", {
                      done: doneCount,
                      total: tasks.length,
                      warnings: warnCount,
                    })
                  : t("company_guide.company.progress_ok", { done: doneCount, total: tasks.length })}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {!loaded && !loadError ? (
          <p className="text-sm text-slate-500">{t("company_guide.company.progress_loading")}</p>
        ) : null}
        {tasks.map((row) => (
          <GuideTaskRow key={row.id} {...row} />
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
        <p>{t("company_guide.company.footer_hint")}</p>
      </div>
    </section>
  );
};
